import { X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";

export function CustomLumberEntry({ item, idx, cli, cl, readOnly, updateItemStopwatch, setRemoveCustomLumber, originalTotal, totalPiecesCut, remainingPieces, extraCantsOver }) {
  const compLen = item.length || 0;
  const pcsPerCant = parseFloat(cl.pcs_per_cant) || 0;
  const cantsUsed = Math.max(0, parseFloat(cl.qty || "") || 0);
  const hasPpc = pcsPerCant > 0;
  const surplusPieces = Math.max(0, (totalPiecesCut || 0) - (originalTotal || 0));
  const isExtra = surplusPieces > 0 && (extraCantsOver || 0) > 0;
  const displayQty = hasPpc
    ? (surplusPieces > 0
        ? (extraCantsOver || 0)
        : Math.round((remainingPieces / pcsPerCant) * 100) / 100)
    : null;
  const wasteText = (() => {
    const cantIn = (parseFloat(cl.length) || 0) * 12;
    if (cantIn > 0 && compLen > 0) {
      const pcsL = Math.floor(cantIn / compLen);
      const w = cantIn - (pcsL * compLen);
      return w > 0 ? `${w}"` : 'None';
    }
    return '—';
  })();

  const updateField = (field, value) => {
    const customs = [...(item.custom_lumber || [])];
    customs[cli] = { ...customs[cli], [field]: value };
    updateItemStopwatch(item.id, { custom_lumber: customs });
  };

  // Real-time inventory adjustment: positive delta = lumber used (deduct), negative = put back
  const autoAdjust = async (deltaCants) => {
    if (!deltaCants) return;
    if (localStorage.getItem('lumber_auto_deduct') === 'off') return;
    try {
      const { data } = await axios.post(`${API}/lumber-inventory/auto-adjust`, {
        thickness: parseFloat(cl.thickness) || null, width: cl.width || null,
        length: parseFloat(cl.length) || null, species: cl.species, amount: -deltaCants,
      });
      if (data && data.adjusted === false) {
        toast.warning("No matching lumber in inventory — nothing was deducted", { id: `no-match-cl-${idx}-${cli}` });
      }
    } catch {}
  };

  const cantsUsedRaw = cl.qty || "";

  return (
    <div className={`p-2 mb-2 border-2 rounded-sm ${hasPpc && cantsUsed > 0 && remainingPieces === 0 ? 'border-green-400 bg-green-50/50' : 'border-orange-400 bg-orange-50/50'}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-orange-700 mb-1">Custom Lumber Option {cli + 1}</p>
      {/* Header: label + Custom tag + remove */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="text-xs font-bold">
            {hasPpc && displayQty !== null && <span className={cantsUsed > 0 && (remainingPieces === 0 || isExtra) ? 'text-green-600' : ''}>{displayQty}{isExtra ? ' extra' : ''} — </span>}
            {cl.thickness ? `${cl.thickness}"` : ''}{cl.width && cl.width !== 'Random' ? ` x ${cl.width}"` : cl.width === 'Random' ? ' x Rndm' : ''} x {cl.length}&apos; {cl.species || ''}
            {hasPpc && <span className="text-[10px] font-normal text-violet-500 ml-1">({pcsPerCant} pcs/cant)</span>}
          </span>
          <span className="text-[9px] font-bold uppercase tracking-wider text-orange-500 bg-orange-50 border border-orange-200 px-1 py-px flex-shrink-0">Custom</span>
        </div>
        {!readOnly && (
          <button onClick={() => setRemoveCustomLumber(cli)} data-testid={`remove-custom-lumber-${idx}-${cli}`}
            className="text-zinc-400 hover:text-red-500"><X className="w-3 h-3" /></button>
        )}
      </div>

      {/* Editable fields (Cutting page) */}
      {!readOnly ? (
        <div className="ml-6 mt-2 space-y-1.5">
          {/* Row 1: Pcs/Cant + Cants Used + Adjust */}
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Pcs/Cant</p>
              <input type="number" value={cl.pcs_per_cant || ""} onChange={(e) => updateField('pcs_per_cant', e.target.value)}
                placeholder="#" className="w-16 h-11 text-base text-center border border-orange-300 bg-white rounded-none focus:border-orange-500 focus:outline-none font-bold"
                data-testid={`custom-lumber-ppc-${idx}-${cli}`} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Cants Used</p>
              <span className="inline-block w-20 h-11 text-lg text-center leading-[44px] border rounded-none font-bold border-violet-300 bg-violet-50 text-zinc-800"
                data-testid={`custom-lumber-qty-${idx}-${cli}`}>{cantsUsedRaw || '—'}</span>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Adjust</p>
              <div className="flex items-center gap-0.5">
                <input type="number" min="1" id={`cl-adj-${idx}-${cli}`} placeholder="#"
                  onInput={(e) => { if (e.target.value.length > 6) e.target.value = e.target.value.slice(0, 6); }}
                  className="w-16 h-11 text-base text-center border border-zinc-200 bg-white focus:border-zinc-400 focus:outline-none" />
                <button onClick={() => {
                  const inp = document.getElementById(`cl-adj-${idx}-${cli}`);
                  const v = parseFloat(inp?.value) || 0; if (v <= 0) return;
                  const curr = parseFloat(cl.qty || "") || 0;
                  const customs = [...(item.custom_lumber || [])];
                  customs[cli] = { ...customs[cli], qty: String(curr + v), adj_plus: (cl.adj_plus || 0) + 1 };
                  updateItemStopwatch(item.id, { custom_lumber: customs });
                  autoAdjust(v);
                  if (inp) inp.value = "";
                }} className="text-lg font-bold px-3 h-11 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 active:bg-zinc-200"
                  data-testid={`cl-adj-add-${idx}-${cli}`}>+</button>
                <button onClick={() => {
                  const inp = document.getElementById(`cl-adj-${idx}-${cli}`);
                  const v = parseFloat(inp?.value) || 0; if (v <= 0) return;
                  const curr = parseFloat(cl.qty || "") || 0;
                  const applied = Math.min(v, curr);
                  const customs = [...(item.custom_lumber || [])];
                  customs[cli] = { ...customs[cli], qty: String(Math.max(0, curr - v)), adj_minus: (cl.adj_minus || 0) + 1 };
                  updateItemStopwatch(item.id, { custom_lumber: customs });
                  autoAdjust(-applied);
                  if (inp) inp.value = "";
                }} className="text-lg font-bold px-3 h-11 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 active:bg-zinc-200"
                  data-testid={`cl-adj-sub-${idx}-${cli}`}>−</button>
              </div>
              {((cl.adj_plus || 0) > 0 || (cl.adj_minus || 0) > 0) && (
                <div className="flex items-center gap-2 mt-0.5 text-[9px] text-zinc-400">
                  {(cl.adj_plus || 0) > 0 && <span className="text-green-600">+{cl.adj_plus} adds</span>}
                  {(cl.adj_minus || 0) > 0 && <span className="text-red-500">−{cl.adj_minus} subs</span>}
                </div>
              )}
            </div>
          </div>
          {/* Row 2: Waste + Species */}
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Waste</p>
              <span className="text-xs font-semibold text-zinc-800">{wasteText}</span>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Species</p>
              <select value={cl.species} onChange={(e) => updateField('species', e.target.value)}
                className="h-6 text-[11px] border border-violet-300 bg-white rounded-none focus:outline-none max-w-[140px]"
                data-testid={`custom-lumber-species-${idx}-${cli}`}>
                <option value="Pine">Pine</option><option value="Southern Yellow Pine">Southern Yellow Pine</option><option value="Poplar">Poplar</option><option value="Hardwood">Hardwood</option>
              </select>
            </div>
          </div>
          {/* Dimension selectors row */}
          <div className="w-full flex items-end gap-2 pt-1 border-t border-zinc-100 mt-1">
            <div className="flex-1">
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-0.5">Thick</p>
              <select value={cl.thickness} onChange={(e) => updateField('thickness', e.target.value)}
                className="w-full h-6 text-[10px] border border-zinc-300 bg-white rounded-none focus:outline-none">
                <option value="1.5">1.5&quot;</option><option value="3.5">3.5&quot;</option><option value="5.5">5.5&quot;</option><option value="6">6&quot;</option>
              </select>
            </div>
            <div className="flex-1">
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-0.5">Width</p>
              <select value={cl.width} onChange={(e) => updateField('width', e.target.value)}
                className="w-full h-6 text-[10px] border border-zinc-300 bg-white rounded-none focus:outline-none">
                <option value="6">6&quot;</option><option value="5.5">5.5&quot;</option><option value="8">8&quot;</option><option value="Random">Rndm</option>
              </select>
            </div>
            <div className="flex-1">
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-0.5">Length</p>
              <select value={cl.length} onChange={(e) => updateField('length', e.target.value)}
                className="w-full h-6 text-[10px] border border-zinc-300 bg-white rounded-none focus:outline-none">
                <option value="2">2&apos;</option><option value="4">4&apos;</option><option value="5">5&apos;</option><option value="6">6&apos;</option><option value="7">7&apos;</option><option value="8">8&apos;</option><option value="10">10&apos;</option><option value="12">12&apos;</option><option value="14">14&apos;</option><option value="16">16&apos;</option>
              </select>
            </div>
          </div>
        </div>
      ) : (
        /* Read-only view (Office page) — matches regular lumber layout */
        <div className="flex items-start gap-4 ml-6 mt-2 flex-wrap">
          {hasPpc && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Pcs/Cant</p>
            <span className="text-xs font-bold text-zinc-800">{pcsPerCant}</span>
          </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Cants Used</p>
            <span className="text-xs font-bold text-zinc-800">{cantsUsedRaw || '—'}</span>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Species</p>
            <span className="text-xs font-semibold text-zinc-800">{cl.species || '—'}</span>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Waste</p>
            <span className="text-xs font-semibold text-zinc-800">{wasteText}</span>
          </div>
        </div>
      )}
    </div>
  );
}
